import { DeleteOutlined, EditOutlined, PersonFilledOutlined, ShowOutlined } from '@icon';
import { Table, TableProps } from '@ui';
import dayjs from 'dayjs';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import type { ColumnWithLooseAccessor, ColumnWithStrictAccessor } from 'react-table';
import { useTheme } from 'styled-components';
import { Full } from 'types/common';
import { IWorkspaceDetail, SearchWorkspaceParams } from 'types/workspace.types';
import { getShortDateFormat, getTimeFormat } from '../../../../helpers/utils-helpers';

type TTableProps = Full<TableProps<IWorkspaceDetail>>;

export const INITIAL_TABLE_STATE: TTableProps['initialState'] = {
  sortBy: [{ id: 'createdAt', desc: true }],
  pageIndex: 0,
  pageSize: 30,
};

export const INITIAL_SEARCH_PARAMS: SearchWorkspaceParams = {
  sortBy: 'createdAt',
  direction: 'DESC',
  name: null,
  page: 1,
  pageSize: 30,
};

type TColumn =
  | ColumnWithLooseAccessor<IWorkspaceDetail>
  | ColumnWithStrictAccessor<IWorkspaceDetail>;

interface GetTableColumnParams {
  onDetailClick: (rowValues: any) => void;
  onDeleteClick: (rowValues: any) => void;
  canEdit?: boolean;
}

export function useTableColumns(params: GetTableColumnParams) {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const theme = useTheme();

  const { onDeleteClick, onDetailClick, canEdit } = params;

  return React.useMemo<TColumn[]>(
    () => [
      {
        Header: formatMessage({ id: 'departments_table_title' }),
        id: 'name',
        accessor: 'name',
      },
      {
        Header: formatMessage({ id: 'departments_table_institution' }),
        id: 'workspace',
        // eslint-disable-next-line react/display-name
        Cell: ({ row }: any) => {
          return row.original.institutionName;
        },
      },
      {
        Header: 'Počet zamestnancov',
        id: 'userCount',
        Cell: ({ row }: any) => {
          return <>{row.original.workersCount ?? 0}</>;
        },
      },
      {
        Header: formatMessage({ id: 'staff_table_created' }),
        accessor: 'createdAt',
        id: 'createdAt',

        Cell: ({ row }: any) => {
          return (
            <span>
              {dayjs(row.original.createdAt).format(`${getShortDateFormat()} ${getTimeFormat()}`)}
            </span>
          );
        },
      },
      {
        Header: '',
        id: 'edit',
        Cell: ({ row }: any) => {
          if (!canEdit) {
            return null;
          }

          return (
            <Table.Actions
              items={[
                {
                  key: 'detail',
                  label: formatMessage({ id: 'table_context_menu_detail' }),
                  Icon: ShowOutlined,
                  onClick: () => onDetailClick(row.original),
                },
                {
                  key: 'staff-edit',
                  label: `${formatMessage({ id: 'table_context_menu_edit' })} zamestnancov`,
                  Icon: PersonFilledOutlined,
                  onClick: () => {
                    history.push(`/business/profile/departments/staff/${row.original.id}`);
                  },
                },
                {
                  key: 'edit',
                  label: formatMessage({ id: 'table_context_menu_edit' }),
                  Icon: EditOutlined,
                  onClick: () => {
                    history.push(
                      `/business/profile/departments/create-workspace/${row.original.id}`,
                    );
                  },
                },
                // { type: 'divider' },
                // {
                //   key: 'delete',
                //   variant: 'danger',
                //   label: formatMessage({ id: 'table_context_menu_remove' }),
                //   Icon: DeleteOutlined,
                //   onClick: () => onDeleteClick(row.original),
                // },
              ]}
            />
          );
        },
      },
    ],
    [formatMessage, onDetailClick, onDeleteClick, theme, canEdit],
  );
}
